/**
 * Vue Router Configuration
 * Defines all 26 application routes
 */

import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes = [
  // Default redirect
  {
    path: '/',
    redirect: '/workspace',
  },

  // Company Section (4 pages)
  {
    path: '/company/news',
    name: 'company-news',
    component: () => import('@/views/company/NewsView.vue'),
    meta: { section: 'company', subsection: 'news', title: 'News & Updates', requiresPermission: true },
  },
  {
    path: '/company/compliance',
    name: 'company-compliance',
    component: () => import('@/views/company/ComplianceView.vue'),
    meta: { section: 'company', subsection: 'compliance', title: 'Compliance', requiresPermission: true },
  },
  {
    path: '/company/resources',
    name: 'company-resources',
    component: () => import('@/views/company/ResourcesView.vue'),
    meta: { section: 'company', subsection: 'resources', title: 'Tools', requiresPermission: true },
  },
  {
    path: '/company/orgchart',
    name: 'company-orgchart',
    component: () => import('@/views/company/OrgChartView.vue'),
    meta: { section: 'company', subsection: 'orgchart', title: 'OrgChart', requiresPermission: true },
  },
  {
    path: '/company/okrs',
    name: 'company-okrs',
    component: () => import('@/views/company/CompanyOkrsView.vue'),
    meta: { section: 'company', subsection: 'okrs', title: 'Company OKRs', requiresPermission: true },
  },
  {
    path: '/company/feedback-insights',
    name: 'company-feedback-insights',
    component: () => import('@/views/company/FeedbackInsightsView.vue'),
    meta: { section: 'company', subsection: 'feedback-insights', title: 'Feedback Insights', requiresPermission: true },
  },
  {
    path: '/company/wall',
    name: 'company-wall',
    component: () => import('@/views/company/CompanyWallView.vue'),
    meta: { section: 'company', subsection: 'news', title: 'Company Wall', requiresPermission: true },
  },

  // Team Management Section (4 pages)
  {
    path: '/team/directory',
    name: 'team-directory',
    component: () => import('@/views/team/DirectoryView.vue'),
    meta: { section: 'team', subsection: 'directory', requiresPermission: true, title: 'Team Directory' },
  },
  {
    path: '/team/timesheet-approvals',
    name: 'team-timesheet-approvals',
    component: () => import('@/views/team/TimesheetApprovalsView.vue'),
    meta: { section: 'team', subsection: 'timesheet-approvals', requiresPermission: true, title: 'Timesheet Approvals' },
  },
  {
    path: '/team/leave-approvals',
    name: 'team-leave-approvals',
    component: () => import('@/views/team/LeaveApprovalsView.vue'),
    meta: { section: 'team', subsection: 'leave-approvals', requiresPermission: true, title: 'Leave Approvals' },
  },
  {
    path: '/team/performance-reviews',
    name: 'team-performance-reviews',
    component: () => import('@/views/team/PerformanceReviewsView.vue'),
    meta: { section: 'team', subsection: 'performance-reviews', requiresPermission: true, title: 'My Performance' },
  },
  {
    path: '/team/projects',
    name: 'team-projects',
    component: () => import('@/views/team/projects/ProjectsListView.vue'),
    meta: { section: 'team', subsection: 'projects', requiresPermission: true, title: 'Projects' },
  },
  {
    path: '/team/projects/engagement/:id',
    name: 'team-engagement-detail',
    component: () => import('@/views/team/projects/EngagementDetailView.vue'),
    meta: { section: 'team', subsection: 'projects', requiresPermission: true, title: 'Engagement' },
  },
  {
    path: '/team/projects/:id',
    name: 'team-project-detail',
    component: () => import('@/views/team/projects/ProjectDetailView.vue'),
    meta: { section: 'team', subsection: 'projects', requiresPermission: true, title: 'Project Details' },
  },

  // Financials Section
  {
    path: '/financials/allocation',
    name: 'financials-allocation',
    component: () => import('@/views/financials/allocation/AllocationView.vue'),
    meta: { section: 'financials', subsection: 'allocation', requiresPermission: true, title: 'Resource Allocation' },
  },
  {
    path: '/financials/allocation/access-policies',
    name: 'financials-allocation-access-policies',
    component: () => import('@/views/financials/allocation/AccessPoliciesView.vue'),
    meta: { section: 'financials', subsection: 'allocation', requiresAdmin: true, requiresPermission: true, title: 'Allocation Access Policies' },
  },
  {
    path: '/financials/revenue-tracker',
    name: 'financials-revenue-tracker',
    component: () => import('@/views/financials/revenue-tracker/RevenueTrackerView.vue'),
    meta: { section: 'financials', subsection: 'revenue', requiresPermission: true, title: 'Revenue Tracker' },
  },
  {
    path: '/financials/costs',
    name: 'financials-costs',
    component: () => import('@/views/financials/costs/CostsView.vue'),
    meta: { section: 'financials', subsection: 'costs', requiresPermission: true, title: 'Costs' },
  },
  {
    path: '/financials/costs/salaries/add',
    name: 'financials-costs-salaries-add',
    component: () => import('@/views/financials/costs/AddSalaryEntriesView.vue'),
    meta: { section: 'financials', subsection: 'costs', requiresPermission: true, title: 'Add Salary Entries' },
  },
  {
    path: '/financials/analytics',
    name: 'financials-analytics',
    component: () => import('@/views/financials/analytics/AnalyticsView.vue'),
    meta: { section: 'financials', subsection: 'analytics', requiresPermission: true, title: 'Analytics' },
  },

  // My Workspace Section - Single page with all sections
  {
    path: '/workspace',
    name: 'workspace',
    component: () => import('@/views/workspace/WorkspaceView.vue'),
    meta: { section: 'workspace', subsection: 'overview', title: 'My Workspace', requiresPermission: false },
  },
  // Redirect old workspace routes to main workspace with hash
  {
    path: '/workspace/overview',
    redirect: '/workspace#overview'
  },
  {
    path: '/workspace/experience',
    redirect: '/workspace#experience'
  },
  {
    path: '/workspace/education',
    redirect: '/workspace#education'
  },
  {
    path: '/workspace/performance',
    redirect: '/workspace#performance'
  },
  {
    path: '/workspace/timesheets',
    redirect: '/workspace#timesheets'
  },
  {
    path: '/workspace/leave',
    redirect: '/workspace#leave'
  },
  {
    path: '/workspace/compensation',
    redirect: '/workspace#compensation'
  },
  {
    path: '/workspace/documents',
    redirect: '/workspace#documents'
  },
  {
    path: '/workspace/messages',
    redirect: '/workspace#messages'
  },
  {
    path: '/workspace/my-network',
    redirect: '/workspace#my-network'
  },

  // Connections (redirect to Workspace -> My Network tab)
  {
    path: '/connections',
    name: 'connections',
    redirect: '/workspace#my-network',
  },

  // User Profile View (public profile of another user)
  // Note: No section/subsection - profile viewing is not tied to section activation
  {
    path: '/profile/:userId',
    name: 'user-profile',
    component: () => import('@/views/network/ProfileView.vue'),
    meta: { title: 'User Profile' },
  },

  // User Settings (notification preferences, etc.)
  {
    path: '/settings',
    name: 'user-settings',
    component: () => import('@/views/settings/UserSettingsView.vue'),
    meta: { title: 'User Settings', requiresPermission: false },
  },

  // Delivery Portal Section
  {
    path: '/delivery',
    name: 'delivery-portal',
    component: () => import('@/views/delivery/DeliveryPortalView.vue'),
    meta: { section: 'delivery', title: 'Delivery Portal', requiresPermission: true },
  },
  // Portal project detail (per-tab access enforced by portal tab permissions)
  {
    path: '/delivery/projects/:id',
    name: 'delivery-project-detail',
    component: () => import('@/views/team/projects/ProjectDetailView.vue'),
    meta: { section: 'delivery', title: 'Delivery Portal', requiresPermission: true, portalMode: true, portalSlug: 'projects' },
  },
  // Dynamic route for all portal subsections
  // The subsection meta is set dynamically from the slug parameter
  {
    path: '/delivery/:slug',
    name: 'delivery-subsection',
    component: () => import('@/views/delivery/PortalSubsectionView.vue'),
    meta: { section: 'delivery', title: 'Delivery Portal', requiresPermission: true },
    // Dynamic subsection permission check happens in the component
    // since the slug determines which subsection to check
    beforeEnter: (to, from, next) => {
      // Set subsection in meta from slug for permission checking
      to.meta.subsection = to.params.slug;
      next();
    }
  },

  // Administration Section (5 pages) - Admin Only
  {
    path: '/admin/dashboard',
    name: 'admin-dashboard',
    component: () => import('@/views/admin/DashboardView.vue'),
    meta: { section: 'admin', subsection: 'dashboard', requiresAdmin: true, requiresPermission: true, title: 'Admin Dashboard' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UsersView.vue'),
    meta: { section: 'admin', subsection: 'users', requiresAdmin: true, requiresPermission: true, title: 'User Management' },
  },
  {
    path: '/admin/users/create',
    name: 'admin-users-create',
    component: () => import('@/views/admin/UserFormView.vue'),
    meta: { section: 'admin', subsection: 'users', requiresAdmin: true, requiresPermission: true, title: 'Create User' },
  },
  {
    path: '/admin/users/:id/edit',
    name: 'admin-users-edit',
    component: () => import('@/views/admin/UserFormView.vue'),
    meta: { section: 'admin', subsection: 'users', requiresAdmin: true, requiresPermission: true, title: 'Edit User' },
  },
  {
    path: '/admin/access',
    name: 'admin-access',
    component: () => import('@/views/admin/AccessControlView.vue'),
    meta: { section: 'admin', subsection: 'access', requiresAdmin: true, requiresPermission: true, title: 'Access Control' },
  },
  {
    path: '/admin/audit',
    name: 'admin-audit',
    component: () => import('@/views/admin/AuditLogsView.vue'),
    meta: { section: 'admin', subsection: 'audit', requiresAdmin: true, requiresPermission: true, title: 'Audit Logs' },
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('@/views/admin/SettingsView.vue'),
    meta: { section: 'admin', subsection: 'settings', requiresAdmin: true, requiresPermission: true, title: 'Admin Settings' },
  },
  {
    path: '/admin/certifications',
    name: 'admin-certifications',
    component: () => import('@/views/admin/CertificationCatalogView.vue'),
    meta: { section: 'admin', subsection: 'certifications', requiresAdmin: true, requiresPermission: true, title: 'Certification Catalog' },
  },
  {
    path: '/admin/delivery-portal',
    name: 'admin-delivery-portal',
    component: () => import('@/views/admin/DeliveryPortalSettingsView.vue'),
    meta: { section: 'admin', subsection: 'delivery-portal', requiresAdmin: true, title: 'Delivery Portal Settings' },
  },

  // Authentication
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, title: 'Login' },
  },

  // Install App (PWA installation instructions)
  {
    path: '/install-app',
    name: 'install-app',
    component: () => import('@/views/InstallAppView.vue'),
    meta: { title: 'Install App' },
  },

  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, title: 'Page Not Found' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guard for authentication and authorization
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Set page title
  document.title = to.meta.title ? `${to.meta.title} - Intranet` : 'Intranet';

  // Public routes (login, 404)
  if (to.meta.public) {
    return next();
  }

  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    // Try to check auth status from server (silent mode to avoid flickering)
    const authenticated = await authStore.checkAuth(true);

    if (!authenticated) {
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }
  }

  // Check admin-only routes (admins always bypass permission checks)
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    // Redirect non-admin users to workspace
    return next({ name: 'workspace' });
  }

  // Check section activation (applies to everyone including admins)
  if (to.meta.section && to.meta.subsection) {
    // Check if the main section is activated
    const sectionActive = authStore.isSectionActive(to.meta.section, null);
    // Check if the subsection is activated
    const subsectionActive = authStore.isSectionActive(to.meta.section, to.meta.subsection);

    if (!sectionActive || !subsectionActive) {
      // Redirect to workspace (which should be activated)
      return next({ name: 'workspace' });
    }
  }

  // Check permission-based routes
  if (to.meta.requiresPermission && to.meta.section && to.meta.subsection) {
    // Admins have access to everything
    if (!authStore.isAdmin) {
      const hasAccess = authStore.hasPermission(to.meta.section, to.meta.subsection);

      if (!hasAccess) {

        // Check if user has any permissions at all
        const hasAnyPermissions = authStore.userPermissions && authStore.userPermissions.length > 0;

        if (!hasAnyPermissions) {
          // User has no permissions - redirect to workspace
          console.error('User has no permissions assigned');
          return next({ name: 'workspace' });
        }

        // Try to redirect to first available workspace page
        const fallbackRoutes = [
          'workspace',
          'workspace-timesheets',
          'workspace-leave',
          'workspace-documents',
          'workspace-experience',
          'workspace-education',
          'workspace-performance',
          'workspace-compensation',
          'workspace-messages'
        ];

        for (const routeName of fallbackRoutes) {
          const route = router.resolve({ name: routeName });
          if (route && route.meta.section && route.meta.subsection) {
            if (authStore.hasPermission(route.meta.section, route.meta.subsection)) {
              return next({ name: routeName });
            }
          }
        }

        // Try company section routes
        const companyRoutes = ['company-news', 'company-compliance', 'company-resources', 'company-orgchart'];
        for (const routeName of companyRoutes) {
          const route = router.resolve({ name: routeName });
          if (route && route.meta.section && route.meta.subsection) {
            if (authStore.hasPermission(route.meta.section, route.meta.subsection)) {
              return next({ name: routeName });
            }
          }
        }

        // If still no accessible route found, redirect to workspace with a flag
        // The overview page can show a "no permissions" message
        return next({ name: 'workspace', query: { noAccess: 'true' } });
      }
    }
  }

  // All checks passed
  next();
});

export default router;
